import React from "react";
import { BookmarkIcon } from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkSolidIcon } from "@heroicons/react/24/solid";

export interface Choice {
  id: string;
  text: string;
}

export interface ExamQuestionPanelUIProps {
  // Question data
  questionNumber: number;
  questionText: string;
  answerType: "multiple_choice" | "short_answer";
  choices?: Choice[];

  // Answer states
  selectedChoice?: string;
  excludedChoices?: string[];
  markForReview?: boolean;
  shortAnswer?: string;

  // Result mode specific
  correctChoiceId?: string;
  userAnswer?: string;
  isCorrect?: boolean;
  category?: string;

  // Mode and display
  mode?: "exam" | "result" | "demo";
  excludeMode?: boolean;
  showAnswers?: boolean; // for demo mode

  // Event handlers
  onSelectChoice?: (choiceId: string) => void;
  onExcludeChoice?: (choiceId: string) => void;
  onToggleMarkForReview?: () => void;
  onToggleExcludeMode?: () => void;
  onShortAnswerChange?: (value: string) => void;

  // Custom content
  highlightableContent?: React.ReactNode;
  customDirections?: React.ReactNode;
}

const ExamQuestionPanelUI: React.FC<ExamQuestionPanelUIProps> = ({
  questionNumber,
  questionText,
  answerType,
  choices = [],
  selectedChoice,
  excludedChoices = [],
  markForReview = false,
  shortAnswer = "",
  correctChoiceId,
  userAnswer,
  isCorrect,
  category,
  mode = "exam",
  excludeMode = false,
  showAnswers = false,
  onSelectChoice,
  onExcludeChoice,
  onToggleMarkForReview,
  onToggleExcludeMode,
  onShortAnswerChange,
  highlightableContent,
  customDirections,
}) => {
  const isResultMode = mode === "result";
  const isDemoMode = mode === "demo";

  // Get choice letter (A, B, C, D)
  const getChoiceLetter = (idx: number) => String.fromCharCode(65 + idx);

  // Get choice text by choice ID
  const getChoiceText = (choiceId: string) => {
    if (!choices) return choiceId;
    const choice = choices.find((c) => c.id === choiceId);
    return choice ? `${choiceId}. ${choice.text}` : choiceId;
  };

  return (
    <div className="w-1/2 h-full flex flex-col overflow-auto p-8 bg-white">
      {/* Mark for Review Bar */}
      <div className="flex items-center bg-[#ededed] max-w-[650px] mx-auto w-full relative pr-2 cursor-pointer select-none h-8 min-h-[32px]">
        <div className="flex-1 flex items-center" role="button" tabIndex={0}>
          <div className="w-8 h-full flex items-center justify-center bg-black text-white font-bold text-base rounded-none mr-2">
            {questionNumber}
          </div>
          <div
            className={`flex items-center text-sm ${
              markForReview
                ? "font-bold underline text-black"
                : "font-medium text-[#222]"
            }`}
          >
            {mode === "result" ? (
              // Result mode: Show if it was marked for review (read-only)
              <div className="flex items-center text-sm cursor-default">
                {markForReview ? (
                  <BookmarkSolidIcon className="w-4 h-4 mr-1 text-red-600" />
                ) : (
                  <BookmarkIcon className="w-4 h-4 mr-1 text-gray-400" />
                )}
                Mark for Review
              </div>
            ) : (
              // Exam/Demo mode: Interactive button
              <button
                onClick={onToggleMarkForReview}
                className="flex items-center text-sm cursor-pointer"
              >
                {markForReview ? (
                  <BookmarkSolidIcon className="w-4 h-4 mr-1 text-red-600" />
                ) : (
                  <BookmarkIcon className="w-4 h-4 mr-1 text-gray-400" />
                )}
                Mark for Review
              </button>
            )}
          </div>
        </div>
        <div className="flex-1" />

        {/* Exclude Mode Toggle (only in exam/demo mode for multiple choice) */}
        {(mode === "exam" || mode === "demo") &&
          answerType === "multiple_choice" && (
            <button
              type="button"
              aria-label="Toggle Exclude Mode"
              className={`w-9 h-7 flex items-center justify-center border-1 border-black rounded-sm transition-colors ml-2 cursor-pointer my-0.5
              ${
                excludeMode
                  ? "bg-[#3451d6] text-white"
                  : "bg-white text-black hover:bg-gray-200"
              }`}
              onClick={onToggleExcludeMode}
            >
              <img
                src="/ic_abc.png"
                alt="Exclude Mode"
                className={`w-5 h-5 object-contain transition-all filter ${
                  excludeMode ? "invert brightness-200 contrast-200" : ""
                }`}
                style={{
                  filter: !excludeMode ? "grayscale(0) opacity(1)" : undefined,
                }}
              />
            </button>
          )}
      </div>

      <div
        className="w-full max-w-[650px] mx-auto border-b-2 border-black border-dashed"
        style={{ marginBottom: 24 }}
      />

      {/* Question Text */}
      <div className="mb-6 max-w-[650px] mx-auto text-left w-full">
        {highlightableContent ? (
          highlightableContent
        ) : (
          <div className="prose prose-lg leading-relaxed">
            <div
              className="whitespace-pre-wrap text-[18px] text-gray-900 leading-relaxed font-serif font-times [&_img]:block [&_img]:mx-auto"
              style={{ fontFamily: '"Times New Roman", Times, serif' }}
              dangerouslySetInnerHTML={{ __html: questionText }}
            />
          </div>
        )}
      </div>

      {/* Custom Directions (for short answer) */}
      {customDirections && (
        <div className="mb-6 max-w-[650px] mx-auto w-full">
          {customDirections}
        </div>
      )}

      {/* Answer Section */}
      {answerType === "short_answer" ? (
        // Short Answer Input
        <div className="flex flex-col items-start max-w-[650px] mx-auto w-full mb-8">
          <div className="mb-4 text-lg font-medium">Answer:</div>
          {isResultMode ? (
            // Result mode: Show user answer and correct answer
            <div className="w-full space-y-4">
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="text-sm font-medium text-gray-600 mb-2">
                  Your Answer:
                </div>
                <div
                  className={`text-lg ${
                    isCorrect ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {userAnswer || "Not answered"}
                </div>
              </div>
              <div className="p-4 border rounded-lg bg-green-50">
                <div className="text-sm font-medium text-gray-600 mb-2">
                  Correct Answer:
                </div>
                <div className="text-lg text-green-600">
                  {correctChoiceId || "N/A"}
                </div>
              </div>
            </div>
          ) : (
            // Exam/Demo mode: Show input
            <input
              type="text"
              value={shortAnswer}
              onChange={(e) => onShortAnswerChange?.(e.target.value)}
              className="w-full max-w-md px-4 py-3 text-xl border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="Enter your answer"
              inputMode="text"
              readOnly={isResultMode}
            />
          )}
        </div>
      ) : (
        // Multiple Choice Options
        <div className="flex flex-col gap-3 max-w-[650px] mx-auto w-full">
          {choices.map((choice, idx) => {
            const choiceLetter = getChoiceLetter(idx);
            const isSelected = selectedChoice === choice.id;
            const isExcluded = excludedChoices.includes(choice.id);
            const isCorrect = correctChoiceId === choice.id;
            const isUserAnswer = userAnswer === choice.id;

            // Use ExamStartPage styling as base for all modes
            let labelClass =
              "flex items-center px-0 py-0 cursor-pointer transition-all font-sans rounded-lg border pl-4 flex-1 min-h-[56px] relative group ";

            if (isResultMode) {
              // Result mode: use base styling but show user selection
              if (isUserAnswer) {
                labelClass +=
                  "border-blue-700 bg-[#f5f8ff] shadow-[0_0_0_2px_#2563eb] ";
              } else if (isExcluded) {
                labelClass += "border-[#d1d5db] bg-white opacity-60 ";
              } else {
                labelClass += "border-[#d1d5db] bg-white ";
              }
            } else if (isDemoMode && showAnswers) {
              // Demo mode with answers shown: highlight correct answer
              if (isCorrect) {
                labelClass +=
                  "border-blue-700 bg-[#f5f8ff] shadow-[0_0_0_2px_#2563eb] ";
              } else {
                labelClass += "border-[#d1d5db] bg-white ";
              }
            } else {
              // Exam mode styling (same as ExamStartPage)
              if (isSelected) {
                labelClass +=
                  "border-blue-700 bg-[#f5f8ff] shadow-[0_0_0_2px_#2563eb] ";
              } else if (isExcluded) {
                labelClass += "border-[#d1d5db] bg-white opacity-60 ";
              } else {
                labelClass += "border-[#d1d5db] bg-white hover:bg-[#f3f4f6] ";
              }
            }

            return (
              <div key={choice.id} className="flex items-stretch w-full">
                <label
                  className={labelClass}
                  style={{ padding: 0 }}
                  onClick={() => {
                    if (mode === "result") return;
                    // Always select choice when clicking on the choice area
                    onSelectChoice?.(choice.id);
                  }}
                >
                  {/* Choice letter circle - ExamStartPage style */}
                  <span
                    className={`flex items-center justify-center w-6 h-6 rounded-full mr-4 ml-4 mt-0.5 text-base font-semibold text-[14px] transition-all border-1 ${
                      (isResultMode && isUserAnswer) ||
                      (isDemoMode && showAnswers && isCorrect) ||
                      ((mode === "exam" || mode === "demo") && isSelected)
                        ? "bg-blue-700 text-white border-black"
                        : "bg-white text-black border-[#bfc8e6]"
                    }`}
                    style={{ minWidth: 28, minHeight: 28 }}
                  >
                    {choiceLetter}
                  </span>

                  {/* Choice text */}
                  <div className="flex-1 min-w-0 flex items-center h-full w-full">
                    <div
                      className="flex-1 font-normal w-full h-full text-[18px] text-gray-900 leading-relaxed font-serif font-times flex items-center"
                      style={{ fontFamily: '"Times New Roman", Times, serif' }}
                      dangerouslySetInnerHTML={{ __html: choice.text }}
                    />
                  </div>

                  {/* Excluded line through */}
                  {isExcluded && (
                    <span
                      className="absolute left-0 right-0 top-1/2 h-[2px] bg-black"
                      style={{ transform: "translateY(-50%)", zIndex: 2 }}
                    />
                  )}

                  {/* Result mode indicators - positioned at bottom right */}
                  {isResultMode && isCorrect && (
                    <div className="absolute bottom-1 right-2 text-xs text-green-600 font-medium">
                      ✓ Correct
                    </div>
                  )}

                  {/* Demo mode indicators */}
                  {isDemoMode && showAnswers && isCorrect && (
                    <div className="absolute bottom-1 right-2 text-xs text-green-600 font-medium">
                      ✓ Correct
                    </div>
                  )}
                </label>

                {/* Exclude/Undo button (only in exclude mode) */}
                {excludeMode &&
                  (mode === "exam" || mode === "demo") &&
                  (isExcluded ? (
                    <button
                      type="button"
                      className="ml-2 text-xs font-bold text-black underline hover:no-underline self-center bg-transparent border-none p-0 cursor-pointer"
                      style={{ boxShadow: "none" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExcludeChoice?.(choice.id);
                      }}
                    >
                      Undo
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ml-4 flex items-center justify-center w-6 h-6 rounded-full text-[#3451d6] hover:bg-[#e6edf8] cursor-pointer transition-colors self-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        onExcludeChoice?.(choice.id);
                      }}
                      aria-label="Exclude choice"
                    >
                      <span
                        className="relative flex items-center justify-center w-5 h-5 hover:bg-gray-200 rounded-full mr-4 ml-4 mt-0.5 text-base text-[12px] bg-white text-black border-1 border-gray-300 transition-all"
                        style={{ minWidth: 28, minHeight: 28 }}
                      >
                        {choiceLetter}
                        <span
                          className="absolute left-0 right-0 top-1/2 h-[2px] bg-black"
                          style={{ transform: "translateY(-50%)", zIndex: 2 }}
                        />
                      </span>
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExamQuestionPanelUI;
